/* eslint-disable no-param-reassign */
/* DOM manipulation is required here to style and layer the Google Timeline chart. */
import { Dispatch, MutableRefObject, SetStateAction } from "react";

import { ColorConstants } from "../../../../constants";
import { SVGConstants } from "../../../../constants/generic/SVGConstants";
import { PortfolioDatesData } from "../../../../models";
import { ChartReferenceNodes, PIPELINE_MILESTONE_COLOR_MAP, PIPELINE_MILESTONE_COLORS, ViewMode } from "../models";

const createRectangle = (x: string, y: string, height: string, width: string | null, fill: string): SVGRectElement => {
  const rect = document.createElementNS(SVGConstants.SVG_NAMESPACE, "rect");
  rect.setAttribute("x", x);
  rect.setAttribute("y", y);
  rect.setAttribute("height", height);
  rect.setAttribute("width", width || "0");
  rect.setAttribute("fill", fill);
  return rect;
};

const toDate = (value?: Date | string | null): Date | undefined => {
  if (!value) return undefined;
  return value instanceof Date ? value : new Date(value);
};

const placeMarker = (
  startDate: Date,
  markerDate: Date,
  timelineUnit: number,
  height: number,
  svgNode: Element,
  baselineBounds: DOMRect
): void => {
  const markerSpan = markerDate.getTime() - startDate.getTime();

  const markerLine = createRectangle(
    (baselineBounds.x + timelineUnit * markerSpan).toString(),
    "0",
    height.toString(),
    "2",
    ColorConstants.BLUE_200
  );

  svgNode.appendChild(markerLine);
};

const adjustScrollPosition = (
  roadmapRef: MutableRefObject<HTMLDivElement | null>,
  startDate: Date,
  markerDate: Date,
  viewMode: ViewMode,
  baselineBounds: DOMRect,
  timelineUnit: number
): void => {
  const msInAMonth = 30 * 24 * 60 * 60 * 1000;
  let scrollDate = markerDate.getTime();

  switch (viewMode) {
    case ViewMode.Months1:
      scrollDate -= msInAMonth * 0.25;
      break;
    case ViewMode.Months3:
      scrollDate -= msInAMonth * 0.75;
      break;
    case ViewMode.Months6:
      scrollDate -= msInAMonth * 1.5;
      break;
    case ViewMode.Years2:
      scrollDate -= msInAMonth * 6;
      break;
    case ViewMode.Years1:
    default:
      scrollDate -= msInAMonth * 3;
      break;
  }

  const scrollSpan = scrollDate - startDate.getTime();

  if (roadmapRef.current) {
    // eslint-disable-next-line no-param-reassign
    roadmapRef.current.scrollLeft = baselineBounds.x + timelineUnit * scrollSpan;
  }
};

export const addMarkerAndSetScrollPosition = (
  roadmapRef: MutableRefObject<HTMLDivElement | null>,
  viewMode: ViewMode,
  svgNode: SVGGraphicsElement | undefined,
  height: number | undefined,
  data: PortfolioDatesData[],
  showMilestones: boolean
): void => {
  if (!svgNode || !height) return;

  const dates = data.flatMap((item) =>
    Object.values(item.dates)
      .map((phase) => ({
        startDate: toDate(phase.start),
        endDate: toDate(phase.end ?? phase.start),
      }))
      .filter((d) => d.startDate && d.endDate)
      .map((d) => ({
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        startDate: d.startDate!.getTime(),
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        endDate: d.endDate!.getTime(),
      }))
  );

  const milestoneDates = showMilestones
    ? data.flatMap((item) =>
        (item.milestones ?? [])
          .map((m) => toDate(m.date))
          .filter((d): d is Date => !!d)
          .map((d) => ({
            startDate: d.getTime(),
            endDate: d.getTime(),
          }))
      )
    : [];

  dates.push(...milestoneDates);

  if (dates.length === 0) return;

  const startDate = new Date(Math.min(...dates.map((d) => d.startDate)));
  const endDate = new Date(Math.max(...dates.map((d) => d.endDate)));
  const markerDate = new Date();

  const hAxisNode =
    roadmapRef.current?.querySelector(".timeline") ?? roadmapRef.current?.querySelector("g text")?.closest("g");
  const baselineNode = hAxisNode?.querySelector("path");

  const widthAttr = svgNode.getAttribute("width");
  const svgWidth =
    (widthAttr ? parseFloat(widthAttr) : 0) ||
    svgNode.getBoundingClientRect().width ||
    roadmapRef.current?.scrollWidth ||
    0;
  if (!svgWidth) return;

  const baselineBounds = baselineNode ? baselineNode.getBoundingClientRect() : ({ x: 0, width: svgWidth } as DOMRect);
  const timelineUnit = baselineBounds.width / (endDate.getTime() - startDate.getTime());

  placeMarker(startDate, markerDate, timelineUnit, height, svgNode, baselineBounds);
  adjustScrollPosition(roadmapRef, startDate, markerDate, viewMode, baselineBounds, timelineUnit);
};

export const getTopLevelRoadmapSvg = (
  roadmapRef: MutableRefObject<HTMLDivElement | null>
): SVGGraphicsElement | undefined => {
  const svgs = roadmapRef.current?.querySelectorAll("svg");
  let topLevelSvg: SVGGraphicsElement | undefined;

  svgs?.forEach((svg) => {
    if (svg.closest(".PortfolioRoadmapContainer")) {
      topLevelSvg = svg as unknown as SVGGraphicsElement;
    }
  });

  return topLevelSvg;
};

export const mutateBars = (chartReferenceNodes: ChartReferenceNodes, svgNode?: Element): void => {
  if (svgNode && svgNode.childNodes.length > 5) {
    chartReferenceNodes.bars = svgNode.childNodes[3] as SVGElement;
    chartReferenceNodes.highlightBars = svgNode.childNodes[5] as SVGElement;
  }

  [chartReferenceNodes.bars, chartReferenceNodes.highlightBars].forEach((group) => {
    group?.querySelectorAll("rect").forEach((rect) => {
      const currentStroke = rect.getAttribute("stroke");
      if (currentStroke === "#000000") {
        rect.classList.add("select");
      }

      const currentFill = rect.getAttribute("fill");
      const currentWidth = parseFloat(rect.getAttribute("width") ?? "0");
      const isMilestone =
        rect.classList.contains("milestone") ||
        PIPELINE_MILESTONE_COLORS.includes(currentFill ?? "") ||
        currentWidth <= 12;
      if (currentFill) {
        rect.setAttribute("stroke", currentFill);
        if (isMilestone) {
          rect.classList.add("milestone");
          rect.setAttribute("stroke", PIPELINE_MILESTONE_COLOR_MAP[currentFill] ?? currentFill);
        } else {
          rect.setAttribute("rx", "4");
        }
      }

      if (currentWidth < 8) {
        rect.setAttribute("width", "8");
      }
    });
  });
};

export const adjustTimeline = (
  roadmapRef: MutableRefObject<HTMLDivElement | null>,
  chartReferenceNodes: ChartReferenceNodes,
  svgNode?: Element
): void => {
  if (!svgNode || !roadmapRef.current) return;

  const hAxisSvgGroup = roadmapRef.current?.querySelector("g text")?.closest("g");

  if (hAxisSvgGroup == null) return;

  const widthAttr = svgNode.getAttribute("width");
  const svgWidth = widthAttr ? parseFloat(widthAttr) : 0;
  if (!svgWidth) return;

  const timelineBackground = createRectangle(
    "0",
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    (roadmapRef.current!.scrollHeight - 102.7).toString(),
    "100.2",
    svgWidth.toString(),
    ColorConstants.INDIGO_25
  );
  const timelineBackgroundBottomBorder = createRectangle(
    "0",
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    (roadmapRef.current!.scrollHeight - 2.5).toString(),
    "1",
    svgWidth.toString(),
    ColorConstants.GREY_100
  );

  hAxisSvgGroup.insertBefore(timelineBackground, hAxisSvgGroup.childNodes[0]);
  hAxisSvgGroup.insertBefore(timelineBackgroundBottomBorder, hAxisSvgGroup.childNodes[0]);

  const hAxisSvgGroupClone = hAxisSvgGroup.cloneNode(true) as SVGElement;
  hAxisSvgGroupClone.classList.add("timeline");

  hAxisSvgGroup.remove();

  svgNode.appendChild(hAxisSvgGroupClone);
  chartReferenceNodes.timelineClone = hAxisSvgGroupClone;
};

export const updateTooltipPosition = (
  roadmapRef: MutableRefObject<HTMLDivElement | null>,
  chartReferenceNodes: ChartReferenceNodes
): void => {
  if (chartReferenceNodes.tooltipClone && roadmapRef.current) {
    chartReferenceNodes.tooltipClone.style.top = `${roadmapRef.current.scrollTop}px`;
    chartReferenceNodes.tooltipClone.style.left = `${roadmapRef.current.scrollLeft + roadmapRef.current.clientWidth - 433}px`;
  }
};

export const resetChartHeight = (
  rowLabelsRef: MutableRefObject<HTMLDivElement | null>,
  height: number | undefined,
  setHeight: Dispatch<SetStateAction<number | undefined>>,
  shouldResetChartHeight: boolean,
  setShouldResetChartHeight: Dispatch<SetStateAction<boolean>>,
  svgNode: Element | undefined
): void => {
  const nextHeight = rowLabelsRef.current?.scrollHeight;

  if (nextHeight && shouldResetChartHeight) {
    setHeight(nextHeight);
    setShouldResetChartHeight(false);
  }

  const appliedHeight = height ?? nextHeight;
  if (appliedHeight) {
    svgNode?.setAttribute("height", appliedHeight.toString());
    const parentEl = svgNode?.parentNode as HTMLElement | null | undefined;
    if (parentEl) parentEl.style.height = `${appliedHeight}px`;
  }
};

export const onScroll = (
  roadmapRef: MutableRefObject<HTMLDivElement | null>,
  chartReferenceNodes: ChartReferenceNodes,
  height: number | undefined
): void => {
  if (!roadmapRef.current?.scrollTop && !chartReferenceNodes.timelineClone) return;

  if (height !== undefined) {
    chartReferenceNodes.timelineClone?.setAttribute(
      "transform",
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      `translate(0,-${height - roadmapRef.current!.scrollTop - 48})`
    );
  }

  updateTooltipPosition(roadmapRef, chartReferenceNodes);
};
